import type { OutcomeItem } from "@/services/financeiro";

/** Linhas intermediárias exibidas ao expandir OBRA (2.10) ou ADM Obra (2.11). */
export interface TotalInvestidoPlanoBucket {
  key: string;
  title: string;
  total: number;
  parcelCount: number;
  pctOfGrand: number;
  items: OutcomeItem[];
}

export function normalizePlanName(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

type ObraBucketKey = "folha" | "encargos" | "terceirizados" | "insumos" | "outros";

const OBRA_ORDER: ObraBucketKey[] = ["folha", "encargos", "terceirizados", "insumos", "outros"];

const OBRA_TITLE: Record<ObraBucketKey, string> = {
  folha: "Folha de Pagamento (Obra)",
  encargos: "Encargos e outras despesas com pessoal (Obra)",
  terceirizados: "Terceirizados",
  insumos: "Insumos",
  outros: "Outros Custos - Obra",
};

/**
 * Classifica o nome da categoria Sienge em um dos 5 grupos da OBRA.
 * Ordem de teste: mais específicos primeiro; folha de obra por último antes do restante.
 */
export function classifyObraBucket(nameNorm: string): ObraBucketKey {
  const n = nameNorm;
  if (n.includes("insumo")) {
    return "insumos";
  }
  if (
    /\binsumos?\b|material\s+de\s+consumo|\bepi\b|ferramental|\bmaterial\b.*\(obra\)|consumo.*\(obra\)|combustivel.*\(obra\)/.test(
      n,
    )
  ) {
    return "insumos";
  }
  if (/terceiriz|servico\s+tercei|servicos\s+tercei/.test(n)) {
    return "terceirizados";
  }
  if (
    /\bencargo\b|\bfgts\b|\binss\b|previdenc|contribuicao\s+social|\bpis\b.*folha|\bcofins\b.*folha|vale\s+transporte|vale\s+refeicao|vale\s+alimentacao|cipa\b|sesmt|medicina\s+do\s+trabalho|risco\s+ambiental/.test(
      n,
    )
  ) {
    return "encargos";
  }
  if (
    /\bfolha\b|\bpagamento\b.*pessoal|\bsalario\b|\bdiaria\b|mao\s+de\s+obra|pedreiro|empreiteiro|ajudante|\bmestre\b|eletricista|pintor|servente|carpinteiro|encarregado|soldador|armador|britador|aceiro|profissional\s+liberal/.test(
      n,
    )
  ) {
    return "folha";
  }
  return "outros";
}

type AdmObraBucketKey = "folha_adm" | "fixas" | "outras";

const ADM_OBRA_ORDER: AdmObraBucketKey[] = ["folha_adm", "fixas", "outras"];

const ADM_OBRA_TITLE: Record<AdmObraBucketKey, string> = {
  folha_adm: "Folha de Pagamento ADM Obra",
  fixas: "Despesas Fixas Obra",
  outras: "Outras Despesas ADM Obra",
};

export function classifyAdmObraBucket(nameNorm: string): AdmObraBucketKey {
  const n = nameNorm;
  if (
    /despesas?\s+fixas|\bfixas\b.*obra|\baluguel\b.*\(obra\)|\baluguel\b.*obra|\biptu\b|\bcondominio\b|taxa\s+de\s+agua|taxa\s+de\s+luz|\benergia\b.*\(obra\)|\bagua\b.*\(obra\)|telefone.*\(obra\)|internet.*\(obra\)/.test(
      n,
    )
  ) {
    return "fixas";
  }
  if (
    (/folha|pagamento|salario|hora\s+extra|ferias|decimo|\b13[oº]?\s+salario\b|\badicional\b/.test(n) &&
      /adm|administrativ|escritorio/.test(n)) ||
    /folha\s+pagamento\s+adm|adm.*obra.*folha|folha.*adm.*obra/.test(n)
  ) {
    return "folha_adm";
  }
  return "outras";
}

function sortItems(items: OutcomeItem[]): OutcomeItem[] {
  return [...items].sort((a, b) => {
    const bd = (b.paymentDate || b.dueDate || b.issueDate || "").localeCompare(
      a.paymentDate || a.dueDate || a.issueDate || "",
    );
    if (bd !== 0) return bd;
    return b.originalAmount - a.originalAmount;
  });
}

export function buildObraBuckets(
  analyticRows: Array<{ label: string; items: OutcomeItem[] }>,
  grandTotal: number,
): TotalInvestidoPlanoBucket[] {
  const acc = new Map<ObraBucketKey, OutcomeItem[]>();
  for (const k of OBRA_ORDER) acc.set(k, []);

  for (const row of analyticRows) {
    const bucket = classifyObraBucket(normalizePlanName(row.label));
    acc.get(bucket)!.push(...row.items);
  }

  return OBRA_ORDER.map((key) => {
    const items = sortItems(acc.get(key) ?? []);
    const total = items.reduce((s, i) => s + i.originalAmount, 0);
    return {
      key,
      title: OBRA_TITLE[key],
      total,
      parcelCount: items.length,
      pctOfGrand: grandTotal > 0 ? (total / grandTotal) * 100 : 0,
      items,
    };
  });
}

export function buildAdmObraBuckets(
  analyticRows: Array<{ label: string; items: OutcomeItem[] }>,
  grandTotal: number,
): TotalInvestidoPlanoBucket[] {
  const acc = new Map<AdmObraBucketKey, OutcomeItem[]>();
  for (const k of ADM_OBRA_ORDER) acc.set(k, []);

  for (const row of analyticRows) {
    const bucket = classifyAdmObraBucket(normalizePlanName(row.label));
    acc.get(bucket)!.push(...row.items);
  }

  return ADM_OBRA_ORDER.map((key) => {
    const items = sortItems(acc.get(key) ?? []);
    const total = items.reduce((s, i) => s + i.originalAmount, 0);
    return {
      key,
      title: ADM_OBRA_TITLE[key],
      total,
      parcelCount: items.length,
      pctOfGrand: grandTotal > 0 ? (total / grandTotal) * 100 : 0,
      items,
    };
  });
}
